import { httpsCallable } from "firebase/functions";
import { functions } from "../../firebase";

/**
 * Clase utilitaria para crear notificaciones en el dashboard
 */
class NotificationHelper {
  /**
   * Crear una notificación para un usuario específico o para todos los usuarios del dashboard
   * @param {Object} params - Parámetros de la notificación
   * @param {string} params.servicioId - ID del servicio
   * @param {string} params.titulo - Título de la notificación
   * @param {string} params.mensaje - Mensaje de la notificación
   * @param {string} params.modulo - Módulo relacionado ('documentos', 'usuarios', etc.)
   * @param {string} params.destinatario - Email del destinatario (o 'todos')
   * @param {string} params.link - URL opcional para redirigir al hacer clic
   * @param {string} params.clienteId - ID del cliente relacionado (opcional)
   * @param {string} params.tiendaId - ID del servicio/tienda relacionado (opcional)
   * @param {string} params.prioridad - Prioridad ('normal', 'alta', 'urgente')
   * @returns {Promise<Object>} - Resultado de la operación
   */
  static async createNotification({
    servicioId,
    titulo,
    mensaje,
    modulo,
    destinatario = 'todos',
    link = null,
    clienteId = null,
    tiendaId = null,
    prioridad = 'normal'
  }) {
    try {
      const createNotificationFn = httpsCallable(functions, 'createNotificationV1');
      
      const notificationData = {
        servicioId,
        titulo,
        mensaje,
        modulo,
        destinatario,
        prioridad
      };
      
      if (link) {
        notificationData.link = link;
      }
      
      if (clienteId) {
        notificationData.clienteId = clienteId;
      }
      
      if (tiendaId) {
        notificationData.tiendaId = tiendaId;
      }
      
      const result = await createNotificationFn(notificationData);
      return { success: true, data: result.data };
    } catch (error) {
      console.error("Error al crear notificación:", error);
      return { success: false, error: error.message };
    }
  }
  
  /**
   * Crear notificaciones para los usuarios del dashboard según módulo y filtros
   * @param {Object} params - Parámetros de la notificación
   * @param {string} params.servicioId - ID del servicio
   * @param {string} params.titulo - Título de la notificación
   * @param {string} params.mensaje - Mensaje de la notificación
   * @param {string} params.modulo - Módulo relacionado ('documentos', 'usuarios', etc.)
   * @param {string} params.clienteId - ID del cliente relacionado (opcional)
   * @param {string} params.tiendaId - ID del servicio/tienda relacionado (opcional)
   * @param {string} params.link - URL opcional para redirigir al hacer clic
   * @param {string} params.prioridad - Prioridad ('normal', 'alta', 'urgente')
   * @returns {Promise<Object>} - Resultado de la operación
   */
  static async notifyModule({
    servicioId,
    titulo,
    mensaje,
    modulo,
    clienteId = null,
    tiendaId = null,
    link = null,
    prioridad = 'normal'
  }) {
    try {
      const sendModuleNotificationFn = httpsCallable(functions, 'sendModuleNotificationV1');
      
      const notificationData = {
        servicioId,
        titulo,
        mensaje,
        modulo,
        prioridad
      };
      
      if (clienteId) {
        notificationData.clienteId = clienteId;
      }
      
      if (tiendaId) {
        notificationData.tiendaId = tiendaId;
      }
      
      if (link) {
        notificationData.link = link;
      }
      
      const result = await sendModuleNotificationFn(notificationData);
      return { success: true, data: result.data };
    } catch (error) {
      console.error("Error al enviar notificaciones por módulo:", error);
      return { success: false, error: error.message };
    }
  }
  
  /**
   * Crear notificaciones respetando los filtros de configuración de cada usuario
   * @param {Object} params - Parámetros de la notificación
   * @param {string} params.servicioId - ID del servicio
   * @param {string} params.titulo - Título de la notificación
   * @param {string} params.mensaje - Mensaje de la notificación
   * @param {string} params.modulo - Módulo relacionado ('documentos', 'usuarios', etc.)
   * @param {string} params.clienteId - ID del cliente relacionado (opcional)
   * @param {string} params.tiendaId - ID del servicio/tienda relacionado (opcional)
   * @param {string} params.link - URL opcional para redirigir al hacer clic
   * @param {string} params.prioridad - Prioridad ('normal', 'alta', 'urgente')
   * @returns {Promise<Object>} - Resultado de la operación
   */
  static async createFilteredNotification({
    servicioId,
    titulo,
    mensaje,
    modulo,
    clienteId = null,
    tiendaId = null,
    link = null,
    prioridad = 'normal'
  }) {
    try {
      const sendModuleNotificationFn = httpsCallable(functions, 'sendModuleNotificationV1');
      
      const notificationData = {
        servicioId,
        titulo,
        mensaje,
        modulo,
        prioridad
      };
      
      if (clienteId) {
        notificationData.clienteId = clienteId;
      }
      
      if (tiendaId) {
        notificationData.tiendaId = tiendaId;
      }
      
      if (link) {
        notificationData.link = link;
      }
      
      const result = await sendModuleNotificationFn(notificationData);
      return { success: true, data: result.data };
    } catch (error) {
      console.error("Error al enviar notificaciones filtradas:", error);
      return { success: false, error: error.message };
    }
  }
  
  /**
   * Crear una notificación para usuarios del dashboard desde una acción o evento
   * @param {Object} params - Parámetros de la notificación
   * @param {string} params.servicioId - ID del servicio
   * @param {string} params.accion - Tipo de acción ('crear', 'actualizar', 'eliminar', etc.)
   * @param {string} params.modulo - Módulo relacionado ('documentos', 'usuarios', etc.)
   * @param {string} params.itemNombre - Nombre del elemento sobre el que se realizó la acción
   * @param {string} params.destinatario - Email del destinatario (o 'todos')
   * @param {string} params.clienteId - ID del cliente relacionado (opcional)
   * @param {string} params.tiendaId - ID del servicio/tienda relacionado (opcional)
   * @param {string} params.link - URL opcional para redirigir al hacer clic
   * @param {string} params.prioridad - Prioridad ('normal', 'alta', 'urgente')
   * @returns {Promise<Object>} - Resultado de la operación
   */
  static async notifyAction({
    servicioId,
    accion,
    modulo,
    itemNombre,
    destinatario = 'todos',
    clienteId = null,
    tiendaId = null,
    link = null,
    prioridad = 'normal'
  }) {
    // Por defecto, usar la versión filtrada que respeta la configuración del usuario
    // Solo usar la versión no filtrada si se especifica un destinatario específico
    if (destinatario === 'todos') {
      return await this.notifyActionFiltered({
        servicioId,
        accion,
        modulo,
        itemNombre,
        clienteId,
        tiendaId,
        link,
        prioridad
      });
    }
    
    // Mapeo de acciones a títulos
    const accionTitulo = {
      crear: `Nuevo ${modulo} creado`,
      actualizar: `${modulo} actualizado`,
      eliminar: `${modulo} eliminado`,
      asignar: `${modulo} asignado`,
      completar: `${modulo} completado`,
      rechazar: `${modulo} rechazado`,
      aprobar: `${modulo} aprobado`,
      comentar: `Nuevo comentario en ${modulo}`
    };
    
    // Mapeo de acciones a mensajes
    const accionMensaje = {
      crear: `Se ha creado un nuevo ${modulo}: ${itemNombre}`,
      actualizar: `Se ha actualizado el ${modulo}: ${itemNombre}`,
      eliminar: `Se ha eliminado el ${modulo}: ${itemNombre}`,
      asignar: `Se ha asignado el ${modulo}: ${itemNombre}`,
      completar: `Se ha completado el ${modulo}: ${itemNombre}`,
      rechazar: `Se ha rechazado el ${modulo}: ${itemNombre}`,
      aprobar: `Se ha aprobado el ${modulo}: ${itemNombre}`,
      comentar: `Hay un nuevo comentario en el ${modulo}: ${itemNombre}`
    };
    
    const titulo = accionTitulo[accion] || `Acción en ${modulo}`;
    const mensaje = accionMensaje[accion] || `Se ha realizado una acción en el ${modulo}: ${itemNombre}`;
    
    return await this.createNotification({
      servicioId,
      titulo,
      mensaje,
      modulo,
      destinatario,
      clienteId,
      tiendaId,
      link,
      prioridad
    });
  }
  
  /**
   * Crear una notificación para usuarios del dashboard desde una acción o evento respetando filtros
   * @param {Object} params - Parámetros de la notificación
   * @param {string} params.servicioId - ID del servicio
   * @param {string} params.accion - Tipo de acción ('crear', 'actualizar', 'eliminar', etc.)
   * @param {string} params.modulo - Módulo relacionado ('documentos', 'usuarios', etc.)
   * @param {string} params.itemNombre - Nombre del elemento sobre el que se realizó la acción
   * @param {string} params.clienteId - ID del cliente relacionado (opcional)
   * @param {string} params.tiendaId - ID del servicio/tienda relacionado (opcional)
   * @param {string} params.link - URL opcional para redirigir al hacer clic
   * @param {string} params.prioridad - Prioridad ('normal', 'alta', 'urgente')
   * @returns {Promise<Object>} - Resultado de la operación
   */
  static async notifyActionFiltered({
    servicioId,
    accion,
    modulo,
    itemNombre,
    clienteId = null,
    tiendaId = null,
    link = null,
    prioridad = 'normal'
  }) {
    // Mapeo de acciones a títulos
    const accionTitulo = {
      crear: `Nuevo ${modulo} creado`,
      actualizar: `${modulo} actualizado`,
      eliminar: `${modulo} eliminado`,
      asignar: `${modulo} asignado`,
      completar: `${modulo} completado`,
      rechazar: `${modulo} rechazado`,
      aprobar: `${modulo} aprobado`,
      comentar: `Nuevo comentario en ${modulo}`
    };
    
    // Mapeo de acciones a mensajes
    const accionMensaje = {
      crear: `Se ha creado un nuevo ${modulo}: ${itemNombre}`,
      actualizar: `Se ha actualizado el ${modulo}: ${itemNombre}`,
      eliminar: `Se ha eliminado el ${modulo}: ${itemNombre}`,
      asignar: `Se ha asignado el ${modulo}: ${itemNombre}`,
      completar: `Se ha completado el ${modulo}: ${itemNombre}`,
      rechazar: `Se ha rechazado el ${modulo}: ${itemNombre}`,
      aprobar: `Se ha aprobado el ${modulo}: ${itemNombre}`,
      comentar: `Hay un nuevo comentario en el ${modulo}: ${itemNombre}`
    };
    
    const titulo = accionTitulo[accion] || `Acción en ${modulo}`;
    const mensaje = accionMensaje[accion] || `Se ha realizado una acción en el ${modulo}: ${itemNombre}`;
    
    return await this.createFilteredNotification({
      servicioId,
      titulo,
      mensaje,
      modulo,
      clienteId,
      tiendaId,
      link,
      prioridad
    });
  }
  
  /**
   * Marcar una notificación como leída
   * @param {string} notificationId - ID de la notificación
   * @param {string} servicioId - ID del servicio
   * @param {string} tipo - Tipo de notificación ('dashboard' o 'formularios')
   * @returns {Promise<Object>} - Resultado de la operación
   */
  static async markAsRead(notificationId, servicioId, tipo = 'dashboard') {
    try {
      const markNotificationRead = httpsCallable(functions, 'markNotificationReadV1');
      const result = await markNotificationRead({
        notificationId,
        servicioId,
        tipo
      });
      return { success: true, data: result.data };
    } catch (error) {
      console.error('Error al marcar notificación como leída:', error);
      return { success: false, error: error.message };
    }
  }
  
  /**
   * Marca todas las notificaciones como leídas
   * @param {string} servicioId - ID del servicio
   * @param {string} tipo - Tipo de notificación ('dashboard' o 'formularios')
   * @returns {Promise<Object>} - Resultado de la operación
   */
  static async markAllAsRead(servicioId, tipo = 'dashboard') {
    try {
      const markAllNotificationsRead = httpsCallable(functions, 'markAllNotificationsReadV1');
      const result = await markAllNotificationsRead({
        servicioId,
        tipo
      });
      return { success: true, data: result.data };
    } catch (error) {
      console.error('Error al marcar todas las notificaciones como leídas:', error);
      return { success: false, error: error.message };
    }
  }
  
  /**
   * Elimina una notificación
   * @param {string} notificationId - ID de la notificación
   * @param {string} servicioId - ID del servicio
   * @param {string} tipo - Tipo de notificación ('dashboard' o 'formularios')
   * @returns {Promise<Object>} - Resultado de la operación
   */
  static async deleteNotification(notificationId, servicioId, tipo = 'dashboard') {
    try {
      const deleteNotificationFn = httpsCallable(functions, 'deleteNotificationV1');
      const result = await deleteNotificationFn({
        notificationId,
        servicioId,
        tipo
      });
      return { success: true, data: result.data };
    } catch (error) {
      console.error('Error al eliminar notificación:', error);
      return { success: false, error: error.message };
    }
  }
  
  /**
   * Crear notificación automática para documentos subidos respetando filtros de configuración
   * @param {Object} params - Parámetros de la notificación
   * @param {string} params.servicioId - ID del servicio
   * @param {string} params.documentTypeId - Tipo de documento (ims, legal, etc.)
   * @param {string} params.documentTypeName - Nombre del tipo de documento
   * @param {string} params.cliente - Cliente del documento
   * @param {string} params.tienda - Tienda del documento
   * @param {string} params.vigilante - Email del usuario que subió el documento
   * @param {string} params.documentId - ID del documento creado
   * @returns {Promise<Object>} - Resultado de la operación
   */
  static async notifyDocumentUpload({
    servicioId,
    documentTypeId,
    documentTypeName,
    cliente,
    tienda,
    vigilante,
    documentId
  }) {
    try {
      // Crear notificación usando filtros
      const result = await this.createFilteredNotification({
        servicioId,
        titulo: `Nuevo documento ${documentTypeName} subido`,
        mensaje: `Se ha subido un nuevo documento ${documentTypeName} por el usuario ${vigilante}`,
        modulo: 'documentos',
        clienteId: cliente,
        tiendaId: tienda,
        link: `/documents/${documentId}`,
        prioridad: 'normal'
      });
      
      return result;
    } catch (error) {
      console.error("Error al crear notificación de documento:", error);
      return { success: false, error: error.message };
    }
  }
}

export default NotificationHelper;